import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Field, Form } from 'formik';
import { useTranslation } from 'react-i18next';
import Loading from '../../../components/Loading';
import { onCompanyDetailsSave, onCompanySettingLoad } from '../../../store/Settings/actions';
import { WebsiteField } from './WebsiteField';
import '../setting.css';
import * as yup from 'yup';
import { Input } from '../../../components/Input';
import { FormField } from '../../../components/FormField';

export const CompanyDetails = () => {
    const [data, setData] = useState({
        company_name: '',
        email: '',
        phone: '',
        website: '',
        cheqit_domain: '',
        registered_name: '',
        accounts_email: '',
        reg_number: '',
        vat_id: '',
        registered_adress: '',
        contacts_text: '',
    });

    const dispatch = useDispatch();
    const { t } = useTranslation();

    useEffect(() => {
        dispatch(onCompanySettingLoad());
    }, []);

    const { contacts_text, company_name, email, phone, cheqit_domain, registered_name, accounts_email, reg_number, vat_id, registered_adress, loading } = useSelector(({ settings }) => ({
        loading: settings.loading,
        company_name: settings.company_details.company_name,
        contacts_text: settings.company_details.contacts_text,
        email: settings.company_details.company_email,
        phone: settings.company_details.company_phone,
        cheqit_domain: settings.company_details.cheqit_domain,
        registered_name: settings.company_details.company_registered_name,
        accounts_email: settings.company_details.company_accounts_email,
        reg_number: settings.company_details.company_reg_number,
        vat_id: settings.company_details.company_vat_id,
        registered_adress: settings.company_details.company_registered_adress,
    }));

    useEffect(() => {
        setData((prevState) => ({
            ...prevState,
            company_name,
            email,
            phone,
            cheqit_domain,
            website: `${cheqit_domain}.cheqit.app`,
            registered_name,
            accounts_email,
            reg_number,
            vat_id,
            registered_adress,
            contacts_text
        }));
    }, [contacts_text, company_name, email, phone, cheqit_domain, registered_name, accounts_email, reg_number, vat_id, registered_adress]);

    const validationSchema = yup.object().shape({
        company_name: yup.string().required(t('Required!')),
        email: yup.string().required(t('Required!')),
        accounts_email: yup.string().required(t('Required!')),
        registered_name: yup.string().required(t('Required!')),
        registered_adress: yup.string().required(t('Required!')),
    });

    return (
        <Loading loading={loading}>
            <Formik
                enableReinitialize
                initialValues={data}
                validationSchema={validationSchema}
                validateOnChange={false}
                validateOnBlur={false}
                onSubmit={async (values) => {
                    dispatch(onCompanyDetailsSave(values));
                }}
            >
                {({ errors }) => (
                    <Form>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="rounded-lg shadow-md bg-white pt-5 pr-16 pb-4 pl-5">
                                <p className="font-bold text-base"> {t('General Information')} </p>
                                <span className="text-xs font-medium text-[#676767]">{t('Update your contact information')}</span>

                                <div className="col-span-3 sm:col-span-3 py-2">
                                    <FormField label={t('Company Name')} htmlFor="company_name" required error={errors.company_name}>
                                        <Field type="text" name="company_name" placeholder={t('Name')} className="input mt-1 border-none focus:ring-gray-200  focus:border-gray-100 bg-gray-50 block w-full shadow-sm sm:text-sm rounded-md" />
                                    </FormField>
                                </div>

                                <div className="col-span-3 sm:col-span-3 py-2">
                                    <FormField label={t('Email')} htmlFor="email" required error={errors.email}>
                                        <Field type="email" name="email" placeholder={t('Email')} className="input mt-1 border-none focus:ring-gray-200  focus:border-gray-100 bg-gray-50 block w-full shadow-sm sm:text-sm rounded-md" />
                                    </FormField>
                                </div>

                                <div className="col-span-3 sm:col-span-3 py-2">
                                    <FormField label={t('Phone')} htmlFor="phone">
                                        <Field type="text" name="phone" placeholder={t('Phone')} className="input mt-1 border-none focus:ring-gray-200  focus:border-gray-100 bg-gray-50 block w-full shadow-sm sm:text-sm rounded-md" />
                                    </FormField>
                                </div>
                                <div className="col-span-3 sm:col-span-3 py-2">
                                    <FormField label={t('VAT ID')} htmlFor="vat_id" >
                                        <Field type="text" name="vat_id" placeholder={t('VAT ID')} className="input mt-1 border-none focus:ring-gray-200  focus:border-gray-100 bg-gray-50 block w-full shadow-sm sm:text-sm rounded-md" />
                                    </FormField>
                                </div>
                                <div className="col-span-3 sm:col-span-3 py-2">
                                    <FormField label={t('Website')} htmlFor="cheqit_domain">
                                        <Field name="cheqit_domain" component={WebsiteField} />
                                    </FormField>
                                </div>

                                <div className="col-span-3 sm:col-span-3 py-2">
                                    <FormField label={t('Cheqit Domain')} htmlFor="website">
                                        <Field type="text" name="website" readOnly className="input italic bg-gray-200 pr-12 text-right text-gray-500 mt-1 border-none focus:ring-gray-200 focus:border-gray-100  block w-full shadow-sm sm:text-sm rounded-md" />
                                    </FormField>
                                </div>
                            </div>

                            <div className="rounded-lg shadow-md bg-white pt-5 pr-16 pb-8 pl-5">
                                <p className="font-bold text-base">{t('Company Information')}</p>
                                <span className="text-xs font-medium text-[#676767]">{t('Update your company details')}</span>

                                <div className="col-span-3 sm:col-span-3 py-2">
                                    <FormField label={t('Registered Company Name')} htmlFor="registered_name" required error={errors.registered_name}>
                                        <Field type="text" name="registered_name" placeholder={t('Name')} className="input mt-1 border-none focus:ring-gray-200  focus:border-gray-100 bg-gray-50 block w-full shadow-sm sm:text-sm rounded-md" />
                                    </FormField>
                                </div>

                                <div className="col-span-3 sm:col-span-3 py-2">
                                    <FormField label={t('Accounts Email')} htmlFor="accounts_email" required error={errors.accounts_email}>
                                        <Field type="email" name="accounts_email" placeholder={t('Email')} className="input mt-1 border-none focus:ring-gray-200  focus:border-gray-100 bg-gray-50 block w-full shadow-sm sm:text-sm rounded-md" />
                                    </FormField>
                                </div>

                                <div className="col-span-3 sm:col-span-3 py-2">
                                    <FormField label={t('Company Registration Number')} htmlFor="reg_number" >
                                        <Field type="text" name="reg_number" placeholder={t('Company registration number')} className="input mt-1 border-none focus:ring-gray-200  focus:border-gray-100 bg-gray-50 block w-full shadow-sm sm:text-sm rounded-md" />
                                    </FormField>
                                </div>

                                <div className="col-span-3 sm:col-span-3 py-2">
                                    <FormField label={t('Registered Company Address')} htmlFor="registered_adress" required error={errors.registered_adress}>
                                        <Field type="text" name="registered_adress" placeholder={t('Registered Company Address')} className="input mt-1 border-none focus:ring-gray-200  focus:border-gray-100 bg-gray-50 block w-full shadow-sm sm:text-sm rounded-md" />
                                    </FormField>
                                </div>
                                <div className="col-span-3 sm:col-span-3 py-2">
                                    <FormField label={t('Contacts')} htmlFor="terms_conditions" error={errors.contacts_text}>
                                        <Field as="textarea" type="text" name="contacts_text" placeholder={t('Contacts')} rows="5" className="input p-3 mt-1 border-none focus:ring-gray-200  focus:border-gray-100 bg-gray-50 block w-full shadow-sm sm:text-sm rounded-md" />
                                    </FormField>
                                </div>
                            </div>
                        </div>

                        <div className="py-3 text-right">
                            <button type="submit" className="inline-flex justify-center py-2 px-8 border border-transparent shadow-sm text-sm font-bold rounded-lg text-white bg-gray-900 hover:bg-gray-700 activ:bg-gray-800 focus:outline-none">
                                {t('Save')}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </Loading>
    );
};
